import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import { HR_LEAVE_APPLICATION_TABLE_HEAD } from '../../../components/datagrid/leave/HrLeaveApplicationTableHead';
import dayjs from 'dayjs';
import {
  getAllLeaveApplicationsHR,
  getLeaveDetailsById,
  updateLeaveApplicationBulk,
} from '../../../redux/features/leaveSlice';
import { getAlldepartments } from '../../../redux/features/generalSlice';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import LeaveApprovalDialog from '../../../components/datagrid/leave/LeaveApprovalDialog';

export default function AllLeaveApplications() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { allLeaveApplications, meta, loading, leaveDetails , balanceLeaves } = useSelector(
    (state) => state.leave
  );
  const { alldepartments } = useSelector((state) => state.general);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    checkedRowIds: [],
    frozenColumns: [],
    hiddenColumns: [],
  });

  const [filters, setFilters] = useState({
    year: dayjs().year(),
    month: null,
    department: null,
    status: null,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    status: '',
    remarks: '',
  });
  const [bulkLoading, setBulkLoading] = useState(false);

  const dispatchParams = useMemo(() => {
    return {
      pageNum: tableState.page,
      gridSize: tableState.pageSize,
      status: filters.status,
      department: filters.department,
      month: filters.month,
      year: filters.year,
      search: tableState.search,
    };
  }, [tableState.page, tableState.pageSize, filters, tableState.search]);

  useEffect(() => {
    dispatch(getAllLeaveApplicationsHR(dispatchParams));
  }, [dispatch, dispatchParams]);

  useEffect(() => {
    dispatch(getAlldepartments());
  }, [dispatch]);

  const handleRowClick = (rowData) => {
    dispatch(getLeaveDetailsById(rowData.leaveId));
    setOpenDialog(true);
  };

  const handleBulkApproveClick = () => {
    if (tableState.checkedRowIds.length === 0) return;
    setOpenBulkDialog(true);
  };

  const handleBulkSubmit = async () => {
    setBulkLoading(true);
    try {
      const payload = {
        leaveIds: tableState.checkedRowIds,
        status: bulkFormData.status,
        comments: bulkFormData.remarks,
      };
      const result = await dispatch(updateLeaveApplicationBulk(payload));
      if (result.payload?.status === 'success') {
        enqueueSnackbar(result.payload?.message || 'Leave applications updated successfully', {
          variant: 'success',
        });
        dispatch(getAllLeaveApplicationsHR(dispatchParams));
        setOpenBulkDialog(false);
        setTableState({
          ...tableState,
          checkedRowIds: [],
        });
        setOpenBulkDialog(false);
        setBulkFormData({ status: '', remarks: '' });
      } else {
        enqueueSnackbar(result.payload?.message || 'Error updating leave applications', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error updating leaves:', error);
      enqueueSnackbar(error.message || 'Error updating leave applications', {
        variant: 'error',
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = HR_LEAVE_APPLICATION_TABLE_HEAD();
    return [
      // {
      //   field: 'checkbox',
      //   flex: 0.05,
      //   minWidth: 50,
      //   headerName: '',
      //   type: 'string',
      //   headerAlign: 'center',
      //   align: 'center',
      //   sortable: false,
      //   filterable: false,
      //   renderHeader: () => (
      //     <input
      //       type="checkbox"
      //       checked={
      //         allLeaveApplications?.length > 0 &&
      //         tableState.checkedRowIds.length === allLeaveApplications.length
      //       }
      //       indeterminate={
      //         tableState.checkedRowIds.length > 0 &&
      //         tableState.checkedRowIds.length < allLeaveApplications?.length
      //       }
      //       onChange={(e) => {
      //         if (e.target.checked) {
      //           setTableState({
      //             ...tableState,
      //             checkedRowIds: allLeaveApplications.map((row) => row.leaveId),
      //           });
      //         } else {
      //           setTableState({
      //             ...tableState,
      //             checkedRowIds: [],
      //           });
      //         }
      //       }}
      //       style={{ cursor: 'pointer' }}
      //     />
      //   ),
      //   renderCell: (params) => (
      //     <input
      //       type="checkbox"
      //       checked={tableState.checkedRowIds.includes(params.row.leaveId)}
      //       onChange={(e) => {
      //         if (e.target.checked) {
      //           setTableState({
      //             ...tableState,
      //             checkedRowIds: [...tableState.checkedRowIds, params.row.leaveId],
      //           });
      //         } else {
      //           setTableState({
      //             ...tableState,
      //             checkedRowIds: tableState.checkedRowIds.filter(
      //               (id) => id !== params.row.leaveId
      //             ),
      //           });
      //         }
      //       }}
      //       style={{ cursor: 'pointer' }}
      //     />
      //   ),
      // },
      ...baseColumns,
      {
        field: 'actions',
        flex: 0.08,
        minWidth: 80,
        headerName: 'Actions',
        type: 'string',
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            disabled={params.row.hodStatus === 0 || params.row.hodStatus === -1}
            onClick={() => handleRowClick(params.row)}
          >
            View
          </Button>
        ),
      },
    ];
  }, [tableState.checkedRowIds]);

  const statusOptions = [
    { id: null, label: 'All' },
    { id: 1, label: 'Approved' },
    { id: 2, label: 'Rejected' },
    { id: 4, label: 'Pending Approval from HR' },
    { id: 5, label: 'Pending Approval from Reporting Head' },
    { id: 6, label: 'Bypassed by Reporting Head' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => dayjs().year() - i);

  return (
    <GlobalWrapper title="All Leave Applications">
      <Box sx={{ width: '100%' }}>
        <Box sx={{ marginBottom: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="year-select">Year</InputLabel>
              <Select
                labelId="year-select"
                id="year-select"
                label="Year"
                value={filters.year}
                onChange={(e) =>
                  setFilters({ ...filters, year: e.target.value })
                }
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={['month']}
                label="Month"
                value={filters.month ? dayjs().month(filters.month - 1) : null}
                onChange={(date) =>
                  setFilters({ ...filters, month: date ? date.month() + 1 : null })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="department-select">Department</InputLabel>
              <Select
                labelId="department-select"
                id="department-select"
                label="Department"
                value={filters.department ?? ''}
                onChange={(e) =>
                  setFilters({ ...filters, department: e.target.value || null })
                }
              >
                <MenuItem value="">All Departments</MenuItem>
                {alldepartments?.map((dept) => (
                  <MenuItem key={dept.dept_id} value={dept.dept_id}>
                    {dept.dept_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="status-select">Status</InputLabel>
              <Select
                labelId="status-select"
                id="status-select"
                label="Status"
                value={filters.status ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilters({ ...filters, status: val === '' ? null : val });
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem
                    key={`status-${option.id}`}
                    value={option.id ?? ''}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {tableState.checkedRowIds.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {tableState.checkedRowIds.length} leave(s) selected
            <Button
              size="small"
              variant="contained"
              sx={{ ml: 2 }}
              onClick={handleBulkApproveClick}
            >
              Bulk Approve/Reject
            </Button>
          </Alert>
        )}

        <GlobalTable
          tableState={tableState}
          setTableState={setTableState}
          headers={columns}
          rows={allLeaveApplications}
          checkboxNeeded={true}
          meta={{
            ...meta,
            totalRecords: meta?.totalRecords ?? allLeaveApplications?.length ?? 0,
          }}
          loading={loading}
          rowUniqueId="leaveId"
          exportUrl={
            `leave/emp_leave_status/${filters.status}/${filters.department}/${filters.year}/${filters.month}`
          }
          exportParams={{
            page: 1,
            limit: 'no',
            search: tableState.search,
          }}
        />
      </Box>

      {/* Leave Approval Dialog */}
      <LeaveApprovalDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        leaveData={leaveDetails}
        balanceLeaves={balanceLeaves}
        dispatchParams={dispatchParams}
        loading={loading}
        onSuccess={() => {
          dispatch(getAllLeaveApplicationsHR(dispatchParams));
          setTableState({
            ...tableState,
            checkedRowIds: [],
          });
        }}
      />

      {/* Bulk Approval Dialog */}
      <Dialog open={openBulkDialog} onClose={() => setOpenBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
          Bulk Approve/Reject Leaves
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Alert severity="info">
              You are about to update {tableState.checkedRowIds.length} leave
              application(s)
            </Alert>
            <FormControl fullWidth>
              <InputLabel id="bulk-status-label">Status</InputLabel>
              <Select
                labelId="bulk-status-label"
                id="bulk-status-select"
                value={bulkFormData.status}
                label="Status"
                onChange={(e) =>
                  setBulkFormData({ ...bulkFormData, status: e.target.value })
                }
                required
              >
                <MenuItem value={1}>Approve</MenuItem>
                <MenuItem value={2}>Reject</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks"
              placeholder="Add remarks for bulk action..."
              value={bulkFormData.remarks}
              onChange={(e) =>
                setBulkFormData({ ...bulkFormData, remarks: e.target.value })
              }
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenBulkDialog(false)}
            variant="outlined"
            disabled={bulkLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkSubmit}
            variant="contained"
            color="primary"
            disabled={bulkLoading || !bulkFormData.status}
          >
            {bulkLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </GlobalWrapper>
  );
}
